import React from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Payments as PayoutIcon,
  SwapHoriz as ReverseIcon,
  Add as PlusIcon,
  Timer as OvertimeIcon,
  Calculate as CalcIcon,
  GridOn as GridIcon,
  Block as BadNumberIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard, VisualExample, MiniGrid, Formula, MoneyDisplay } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

export const PayoutsPage: React.FC = () => {
  useDocumentTitle('Payout Options - Support');

  return (
    <SupportLayout
      title="Payout Configuration Guide"
      description="Learn about all the different payout options available in SquareUp, from basic quarter payouts to advanced features like reverse winners and the +N rule."
      breadcrumbs={[{ label: 'Payout Options' }]}
    >
      {/* Overview */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Understanding Payouts
        </Typography>
        <Typography variant="body1" paragraph>
          SquareUp offers flexible payout configurations to match any pool's rules. You can use simple quarter-based payouts or add exciting features like reverse winners and the +N rule.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
          <Typography variant="h6" gutterBottom>Quick Example</Typography>
          <Typography variant="body2" paragraph>
            With a $10/square pool ($1,000 total pot), a typical payout might be:
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <MoneyDisplay label="Q1 Winner" amount={100} variant="positive" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <MoneyDisplay label="Q2/Halftime" amount={200} variant="positive" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <MoneyDisplay label="Q3 Winner" amount={100} variant="positive" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <MoneyDisplay label="Q4/Final" amount={600} variant="positive" />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Right Way Payouts (Quarter Payouts) */}
      <Box id="quarter-payouts" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PayoutIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Right Way Payouts (Quarter Payouts)
        </Typography>

        <Typography variant="body1" paragraph>
          The foundation of every payout configuration. <strong>Right Way payouts</strong> are the main payouts based on the actual score—the "normal" winning square. You set a dollar amount for each quarter (or scoring period). Common splits include:
        </Typography>

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden', mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Split Name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Q1</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Q2</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Q3</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Q4/Final</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Best For</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><strong>Final Heavy (10/10/10/70)</strong></TableCell>
                <TableCell align="center">10%</TableCell>
                <TableCell align="center">10%</TableCell>
                <TableCell align="center">10%</TableCell>
                <TableCell align="center">70%</TableCell>
                <TableCell>Traditional pools</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Balanced (20/20/20/40)</strong></TableCell>
                <TableCell align="center">20%</TableCell>
                <TableCell align="center">20%</TableCell>
                <TableCell align="center">20%</TableCell>
                <TableCell align="center">40%</TableCell>
                <TableCell>More excitement throughout</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Equal (25/25/25/25)</strong></TableCell>
                <TableCell align="center">25%</TableCell>
                <TableCell align="center">25%</TableCell>
                <TableCell align="center">25%</TableCell>
                <TableCell align="center">25%</TableCell>
                <TableCell>Fair distribution</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Halftime Focus (15/35/15/35)</strong></TableCell>
                <TableCell align="center">15%</TableCell>
                <TableCell align="center">35%</TableCell>
                <TableCell align="center">15%</TableCell>
                <TableCell align="center">35%</TableCell>
                <TableCell>Super Bowl pools</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        <VisualExample title="Example: $1,000 Pool with 10/10/10/70 Split">
          <Grid container spacing={3} justifyContent="center">
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">Q1</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">$100</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">Q2/Halftime</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">$100</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">Q3</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">$100</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                <Typography variant="subtitle2" color="success.main" fontWeight="bold">Q4/Final</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">$700</Typography>
              </Paper>
            </Grid>
          </Grid>
        </VisualExample>

        <InfoCard title="Organizer Profit" variant="info" icon={<CalcIcon />}>
          <Typography variant="body2">
            You can optionally set an <strong>organizer profit percentage</strong> (0-50%). This amount is subtracted from the pot before calculating payouts. For example, with a $1,000 pot and 10% profit, $900 goes to winners and $100 to the organizer.
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Wrong Way Payouts */}
      <Box id="reverse-payouts" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <ReverseIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Wrong Way Payouts
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>Wrong Way payouts</strong> (also called "reverse" payouts) pay a bonus to the square where the team scores are <strong>swapped</strong>. If the score is Chiefs 17, Eagles 14, the Right Way winner is row 4, column 7. The <strong>Wrong Way winner</strong> is row 7, column 4.
        </Typography>

        <VisualExample title="Right Way vs Wrong Way Winner Example" caption="Score: Chiefs 17, Eagles 14 — Last digits: 7 and 4">
          <Grid container spacing={4} justifyContent="center" alignItems="center">
            <Grid size="grow">
              <Typography variant="subtitle2" textAlign="center" sx={{ mb: 1 }} color="success.main" fontWeight="bold">
                Right Way Winner
              </Typography>
              <MiniGrid
                rows={5}
                cols={5}
                homeNumbers={[5, 6, 7, 8, 9]}
                awayNumbers={[2, 3, 4, 5, 6]}
                highlighted={[{ row: 2, col: 2, color: '#4caf50', label: 'WIN' }]}
                homeTeam="KC"
                awayTeam="PHI"
              />
              <Typography variant="body2" textAlign="center" sx={{ mt: 1 }}>
                Row 4 x Col 7 = <strong>$100</strong>
              </Typography>
            </Grid>
            <Grid size="grow">
              <Typography variant="subtitle2" textAlign="center" sx={{ mb: 1 }} color="warning.main" fontWeight="bold">
                Wrong Way Winner
              </Typography>
              <MiniGrid
                rows={5}
                cols={5}
                homeNumbers={[2, 3, 4, 5, 6]}
                awayNumbers={[5, 6, 7, 8, 9]}
                highlighted={[{ row: 2, col: 2, color: '#ff9800', label: 'WW' }]}
                homeTeam="KC"
                awayTeam="PHI"
              />
              <Typography variant="body2" textAlign="center" sx={{ mt: 1 }}>
                Row 7 x Col 4 = <strong>$25</strong>
              </Typography>
            </Grid>
          </Grid>
        </VisualExample>

        <InfoCard title="Per-Quarter Wrong Way Amounts" variant="tip">
          <Typography variant="body2">
            You can set <strong>different Wrong Way amounts for each quarter</strong>. For example, pay $10 Wrong Way in Q1-Q3, but $50 Wrong Way for the final score.
          </Typography>
        </InfoCard>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Note:</strong> Wrong Way payouts only apply when the swapped position is different from the Right Way winner. If the score ends in matching digits (e.g., 7-7), there's only one winner.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* +N Rule */}
      <Box id="plus-n-rule" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PlusIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          The +N Rule
        </Typography>

        <Typography variant="body1" paragraph>
          The +N rule (commonly "+5") adds a fixed number to one team's last digit to determine a secondary winner. This creates more excitement and spreads the winnings. You can enable +N for <strong>Right Way</strong>, <strong>Wrong Way</strong>, or <strong>both</strong> independently.
        </Typography>

        <VisualExample title="+5 Rule Example" caption="Score: Chiefs 17, Eagles 14 — Main winner: row 4, col 7">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Normal:</strong> Row (14 → 4) x Column (17 → 7)
            </Typography>
            <Formula
              formula="Away digit + 5 = (4 + 5) mod 10"
              result="Row 9"
              variables={[
                { name: 'Original digit', value: '4', description: 'Eagles last digit' },
                { name: '+N offset', value: '5', description: 'Configured offset' },
                { name: 'New digit', value: '9', description: '(4+5) mod 10' },
              ]}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              So the +5 winner is: <strong>Row 9, Column 7</strong>
            </Typography>
          </Box>
        </VisualExample>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Common +N Values</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="+5 (Most popular)" secondary="Traditional Super Bowl squares rule" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="+3" secondary="Creates more spread winners" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="+7" secondary="Different dynamic" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Right Way vs Wrong Way +N</Typography>
              <Typography variant="body2" paragraph>
                You can enable +N separately for Right Way and Wrong Way payouts:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Right Way +N" secondary="Applies +N to the normal score winner" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Wrong Way +N" secondary="Applies +N to the swapped score winner" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Per-Quarter +N Amounts</Typography>
          <Typography variant="body2" paragraph>
            Like Wrong Way payouts, you can set different +N amounts for each quarter:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="Q1: $5" size="small" />
            <Chip label="Q2: $10" size="small" />
            <Chip label="Q3: $5" size="small" />
            <Chip label="Q4: $25" size="small" color="primary" />
          </Box>
        </Paper>

        <InfoCard title="Combining +N with Wrong Way" variant="tip">
          <Typography variant="body2">
            When both Right Way +N and Wrong Way +N are enabled, you get <strong>four potential winners per quarter</strong>: Right Way, Wrong Way, Right Way +N, and Wrong Way +N. This maximizes excitement but requires a larger pot to fund all payouts.
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Overtime */}
      <Box id="overtime" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <OvertimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Overtime Configuration
        </Typography>

        <Typography variant="body1" paragraph>
          When a game goes to overtime, you can optionally configure a separate payout for the final score. This prevents the Q4 winner from "double-dipping."
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'warning.50', border: 1, borderColor: 'warning.main' }}>
              <Typography variant="h6" gutterBottom>Without Overtime Config</Typography>
              <Typography variant="body2" paragraph>
                Q4 payout goes to the end of regulation winner. If there's overtime, the final score might be different but no additional payout is made.
              </Typography>
              <Chip label="Q4: $700" color="warning" />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'success.50', border: 1, borderColor: 'success.main' }}>
              <Typography variant="h6" gutterBottom>With Overtime Config</Typography>
              <Typography variant="body2" paragraph>
                Configure a separate amount for "Final" when overtime occurs. Q4 pays at end of regulation, Final pays after OT.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label="Q4: $400" />
                <Chip label="Final (OT): $300" color="success" />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 3 }}>
          Overtime config is optional. Most pools just pay Q4 based on the final score, regardless of overtime.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Neighbor Payouts */}
      <Box id="neighbor-payouts" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <GridIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Neighbor Payouts
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>Neighbor payouts</strong> reward squares adjacent to the winning square. This creates more excitement as players root for "near misses" to still win a payout. You can configure which directions pay out and set different amounts per quarter.
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>100-Cell Boards Only:</strong> Neighbor payouts (both regular and box) are only available for standard 100-cell boards. Paired (50-cell) and Quad (25-cell) modes are not compatible with neighbor payouts because neighbor relationships must align with individual square ownership.
        </Alert>

        {/* Direction Configuration Examples */}
        <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>Direction Options</Typography>
        <Grid container spacing={3}>
          {/* Cardinal Example */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
                Cardinal (4 Directions)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 40px)',
                    gridTemplateRows: 'repeat(3, 40px)',
                    gap: '3px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: 'grey.100',
                  }}
                >
                  {['NW', 'N', 'NE', 'W', 'WIN', 'E', 'SW', 'S', 'SE'].map((cell) => {
                    const isCenter = cell === 'WIN';
                    const isCardinal = ['N', 'S', 'E', 'W'].includes(cell);
                    return (
                      <Box
                        key={cell}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 0.5,
                          bgcolor: isCenter ? 'success.main' : isCardinal ? 'info.main' : 'grey.300',
                          color: isCenter || isCardinal ? 'white' : 'text.disabled',
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                        }}
                      >
                        {cell}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                North, South, East, West
              </Typography>
              <Chip label="4 neighbors" size="small" sx={{ display: 'block', mx: 'auto', mt: 1, width: 'fit-content' }} />
            </Paper>
          </Grid>

          {/* Diagonal Example */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
                Diagonal (4 Directions)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 40px)',
                    gridTemplateRows: 'repeat(3, 40px)',
                    gap: '3px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: 'grey.100',
                  }}
                >
                  {['NW', 'N', 'NE', 'W', 'WIN', 'E', 'SW', 'S', 'SE'].map((cell) => {
                    const isCenter = cell === 'WIN';
                    const isDiagonal = ['NE', 'SE', 'SW', 'NW'].includes(cell);
                    return (
                      <Box
                        key={cell}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 0.5,
                          bgcolor: isCenter ? 'success.main' : isDiagonal ? 'info.main' : 'grey.300',
                          color: isCenter || isDiagonal ? 'white' : 'text.disabled',
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                        }}
                      >
                        {cell}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                Northeast, Southeast, Southwest, Northwest
              </Typography>
              <Chip label="4 neighbors" size="small" sx={{ display: 'block', mx: 'auto', mt: 1, width: 'fit-content' }} />
            </Paper>
          </Grid>

          {/* All 8 Example */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
                All 8 Directions
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 40px)',
                    gridTemplateRows: 'repeat(3, 40px)',
                    gap: '3px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: 'grey.100',
                  }}
                >
                  {['NW', 'N', 'NE', 'W', 'WIN', 'E', 'SW', 'S', 'SE'].map((cell) => {
                    const isCenter = cell === 'WIN';
                    return (
                      <Box
                        key={cell}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 0.5,
                          bgcolor: isCenter ? 'success.main' : 'info.main',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                        }}
                      >
                        {cell}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                All surrounding squares
              </Typography>
              <Chip label="8 neighbors" size="small" color="primary" sx={{ display: 'block', mx: 'auto', mt: 1, width: 'fit-content' }} />
            </Paper>
          </Grid>

          {/* Custom/Box Example */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
                Custom (Box Pattern)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 40px)',
                    gridTemplateRows: 'repeat(3, 40px)',
                    gap: '3px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: 'grey.100',
                  }}
                >
                  {['NW', 'N', 'NE', 'W', 'WIN', 'E', 'SW', 'S', 'SE'].map((cell) => {
                    const isCenter = cell === 'WIN';
                    // Example: E, S, SE (box to the southeast)
                    const isBox = ['E', 'S', 'SE'].includes(cell);
                    return (
                      <Box
                        key={cell}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 0.5,
                          bgcolor: isCenter ? 'success.main' : isBox ? 'info.main' : 'grey.300',
                          color: isCenter || isBox ? 'white' : 'text.disabled',
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                        }}
                      >
                        {cell}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                Any custom combination (e.g., E, S, SE)
              </Typography>
              <Chip label="3 neighbors" size="small" sx={{ display: 'block', mx: 'auto', mt: 1, width: 'fit-content' }} />
            </Paper>
          </Grid>

          {/* Custom Box Pattern - NW Corner */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
                Custom (Box Pattern)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 40px)',
                    gridTemplateRows: 'repeat(3, 40px)',
                    gap: '3px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: 'grey.100',
                  }}
                >
                  {['NW', 'N', 'NE', 'W', 'WIN', 'E', 'SW', 'S', 'SE'].map((cell) => {
                    const isCenter = cell === 'WIN';
                    const isBox = ['NW', 'N', 'W'].includes(cell);
                    return (
                      <Box
                        key={cell}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 0.5,
                          bgcolor: isCenter ? 'success.main' : isBox ? 'info.main' : 'grey.300',
                          color: isCenter || isBox ? 'white' : 'text.disabled',
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                        }}
                      >
                        {cell}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                Another box: W, NW, N
              </Typography>
              <Chip label="3 neighbors" size="small" sx={{ display: 'block', mx: 'auto', mt: 1, width: 'fit-content' }} />
            </Paper>
          </Grid>

          {/* Single Neighbor - East */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
                Single Neighbor
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 40px)',
                    gridTemplateRows: 'repeat(3, 40px)',
                    gap: '3px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: 'grey.100',
                  }}
                >
                  {['NW', 'N', 'NE', 'W', 'WIN', 'E', 'SW', 'S', 'SE'].map((cell) => {
                    const isCenter = cell === 'WIN';
                    const isActive = cell === 'E';
                    return (
                      <Box
                        key={cell}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 0.5,
                          bgcolor: isCenter ? 'success.main' : isActive ? 'info.main' : 'grey.300',
                          color: isCenter || isActive ? 'white' : 'text.disabled',
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                        }}
                      >
                        {cell}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                Just one direction: East only
              </Typography>
              <Chip label="1 neighbor" size="small" sx={{ display: 'block', mx: 'auto', mt: 1, width: 'fit-content' }} />
            </Paper>
          </Grid>
        </Grid>

        {/* On-Grid Example */}
        <Box sx={{ mt: 3 }}>
        <VisualExample title="On the Full Board" caption="Main winner in center with cardinal directions enabled">
          <Box sx={{ textAlign: 'center' }}>
            <MiniGrid
              rows={5}
              cols={5}
              homeNumbers={[5, 6, 7, 8, 9]}
              awayNumbers={[2, 3, 4, 5, 6]}
              highlighted={[
                { row: 2, col: 2, color: '#4caf50', label: 'WIN' },
                { row: 1, col: 2, color: '#2196f3', label: 'N' },
                { row: 3, col: 2, color: '#2196f3', label: 'S' },
                { row: 2, col: 1, color: '#2196f3', label: 'W' },
                { row: 2, col: 3, color: '#2196f3', label: 'E' },
              ]}
              homeTeam="KC"
              awayTeam="PHI"
            />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Main winner: <strong>$100</strong> | Each neighbor: <strong>$10</strong>
            </Typography>
          </Box>
        </VisualExample>
        </Box>

        {/* Toroidal Grid Section */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>Toroidal Grid (Wrap-Around)</Typography>
          <Typography variant="body2" paragraph>
            The grid wraps around like a donut (toroid). This means edges connect to opposite edges, so every square has exactly 8 neighbors.
          </Typography>

          <Grid container spacing={3}>
            {/* Wrap-around Visual Example */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom>Corner Example: Top-Left (0,0)</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <MiniGrid
                    rows={5}
                    cols={5}
                    homeNumbers={[0, 1, 2, 3, 9]}
                    awayNumbers={[0, 1, 2, 3, 9]}
                    highlighted={[
                      { row: 0, col: 0, color: '#4caf50', label: 'WIN' },
                      { row: 0, col: 1, color: '#2196f3', label: 'E' },
                      { row: 1, col: 0, color: '#2196f3', label: 'S' },
                      { row: 1, col: 1, color: '#2196f3', label: 'SE' },
                      { row: 4, col: 0, color: '#ff9800', label: 'N' },
                      { row: 0, col: 4, color: '#ff9800', label: 'W' },
                      { row: 4, col: 4, color: '#ff9800', label: 'NW' },
                      { row: 4, col: 1, color: '#ff9800', label: 'NE' },
                      { row: 1, col: 4, color: '#ff9800', label: 'SW' },
                    ]}
                    homeTeam="H"
                    awayTeam="A"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" align="center">
                  <span style={{ color: '#2196f3' }}>Blue</span> = normal neighbors | <span style={{ color: '#ff9800' }}>Orange</span> = wrapped neighbors
                </Typography>
              </Paper>
            </Grid>

            {/* Explanation */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom>How Wrap-Around Works</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Top row's north neighbor" secondary="Wraps to bottom row (row 9)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Bottom row's south neighbor" secondary="Wraps to top row (row 0)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Left column's west neighbor" secondary="Wraps to right column (col 9)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Right column's east neighbor" secondary="Wraps to left column (col 0)" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Payout Configuration</Typography>
          <Typography variant="body2" paragraph>
            You can set neighbor payouts in two ways:
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Global Amount</Typography>
              <Typography variant="body2">
                Set a single dollar amount that applies to all neighbors in every quarter.
              </Typography>
              <Chip label="All: $10 per neighbor" size="small" sx={{ mt: 1 }} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Per-Quarter Amounts</Typography>
              <Typography variant="body2">
                Set different amounts for each quarter, like other payout types.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Chip label="Q1: $5" size="small" />
                <Chip label="Q2: $10" size="small" />
                <Chip label="Q3: $5" size="small" />
                <Chip label="Q4: $20" size="small" color="primary" />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ mt: 3 }}>
          <InfoCard title="Hover to Preview" variant="tip">
            <Typography variant="body2">
              When viewing recorded scores, hover over a quarter's hit card to see which squares would receive neighbor payouts. The grid will highlight both the main winner and all neighbor positions.
            </Typography>
          </InfoCard>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Note:</strong> Neighbor payouts are calculated per neighbor direction. With 4 directions enabled and $10 per neighbor, each quarter could pay up to $40 in neighbor bonuses (if all neighbors are claimed).
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Box Neighbor Payouts */}
      <Box id="box-neighbor-payouts" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <GridIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Box Neighbor Payouts
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>Box Neighbor Payouts</strong> divide the 10×10 grid into fixed rectangular groups. When a square wins, the other squares in the same box also receive a payout. Unlike regular neighbor payouts which use compass directions, box neighbors are determined by fixed position groups.
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Choose One:</strong> You cannot enable both regular (wraparound) neighbor payouts AND box neighbor payouts on the same board. Choose the system that best fits your pool.
        </Alert>

        {/* Box Size Options */}
        <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>Box Size Options</Typography>
        <Grid container spacing={3}>
          {/* 2x2 Boxes */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'info.main', bgcolor: 'info.50' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="info.dark">
                2×2 Boxes (25 groups of 4 squares)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gridTemplateRows: 'repeat(5, 1fr)',
                    gap: '3px',
                    width: 180,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.75,
                    bgcolor: 'background.paper',
                  }}
                >
                  {Array.from({ length: 25 }).map((_, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        aspectRatio: '1',
                        bgcolor: idx % 2 === (Math.floor(idx / 5) % 2) ? 'info.light' : 'info.main',
                        borderRadius: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      4
                    </Box>
                  ))}
                </Box>
              </Box>
              <Typography variant="body2" paragraph>
                Each 2×2 box contains <strong>4 squares</strong>. When one wins, the other <strong>3 neighbors</strong> in that box get paid.
              </Typography>
              <Chip label="3 neighbors per winner" color="info" size="small" sx={{ mr: 1 }} />
              <Chip label="25 box groups" size="small" variant="outlined" />
            </Paper>
          </Grid>

          {/* 5x2 Boxes */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                5×2 Boxes (10 groups of 10 squares)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gridTemplateRows: 'repeat(5, 1fr)',
                    gap: '3px',
                    width: 180,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.75,
                    bgcolor: 'background.paper',
                  }}
                >
                  {Array.from({ length: 10 }).map((_, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        aspectRatio: '2.5',
                        bgcolor: idx % 2 === (Math.floor(idx / 2) % 2) ? 'grey.400' : 'grey.600',
                        borderRadius: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      10
                    </Box>
                  ))}
                </Box>
              </Box>
              <Typography variant="body2" paragraph>
                Each 5×2 box contains <strong>10 squares</strong>. When one wins, the other <strong>9 neighbors</strong> in that box get paid.
              </Typography>
              <Chip label="9 neighbors per winner" size="small" sx={{ mr: 1 }} />
              <Chip label="10 box groups" size="small" variant="outlined" />
            </Paper>
          </Grid>

          {/* 5x5 Quadrants */}
          <Grid size={12}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'secondary.main', bgcolor: 'secondary.50' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="secondary.dark">
                5×5 Quadrants (4 groups of 25 squares)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gridTemplateRows: 'repeat(2, 1fr)',
                    gap: '4px',
                    width: 200,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.75,
                    bgcolor: 'background.paper',
                  }}
                >
                  {['TL', 'TR', 'BL', 'BR'].map((label, idx) => (
                    <Box
                      key={label}
                      sx={{
                        aspectRatio: '1',
                        bgcolor: idx % 2 === (Math.floor(idx / 2) % 2) ? 'secondary.light' : 'secondary.main',
                        borderRadius: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                      }}
                    >
                      25
                    </Box>
                  ))}
                </Box>
              </Box>
              <Typography variant="body2" paragraph>
                The grid is divided into <strong>4 quadrants</strong> of 25 squares each (top-left, top-right, bottom-left, bottom-right). When one square wins, all <strong>24 other squares</strong> in the same quadrant get paid. This creates the largest "team" payouts.
              </Typography>
              <Chip label="24 neighbors per winner" color="secondary" size="small" sx={{ mr: 1 }} />
              <Chip label="4 quadrant groups" size="small" variant="outlined" />
            </Paper>
          </Grid>
        </Grid>

        {/* Example Calculation */}
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'primary.200', bgcolor: 'primary.50', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Example Calculation (2×2 boxes)</Typography>
          <Typography variant="body2" paragraph>
            With a <strong>$5 per neighbor per quarter</strong> payout and a 4-quarter game:
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <MoneyDisplay label="Per neighbor/quarter" amount={5} variant="neutral" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <MoneyDisplay label="× Neighbors per box" amount={3} variant="neutral" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <MoneyDisplay label="= Per quarter total" amount={15} variant="positive" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <MoneyDisplay label="Full game (×4 quarters)" amount={60} variant="positive" />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Each neighbor receives the <strong>full</strong> configured amount — the payout is NOT split among neighbors.
          </Typography>
        </Paper>

        {/* Key Differences */}
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Box Neighbors vs. Wraparound Neighbors</Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Feature</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Box Neighbors</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Wraparound Neighbors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Position</TableCell>
                <TableCell>Fixed groups (same squares always together)</TableCell>
                <TableCell>Relative to winner (depends on winning square)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Neighbor count</TableCell>
                <TableCell>3 (2×2), 9 (5×2), or 24 (5×5)</TableCell>
                <TableCell>1-8 (configurable directions)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Visual indicator</TableCell>
                <TableCell>Colored borders on grid</TableCell>
                <TableCell>No visible indicator until score recorded</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Best for</TableCell>
                <TableCell>Groups who want visible "teams" of squares</TableCell>
                <TableCell>Traditional neighbor payout systems</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        <Box sx={{ mt: 3 }}>
          <InfoCard title="Visual Boundaries" variant="tip">
            <Typography variant="body2">
              When box neighbors are enabled, you'll see colored borders on the grid showing each box group. The border color matches your board's theme for a clean look.
            </Typography>
          </InfoCard>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Bad Numbers */}
      <Box id="bad-numbers" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <BadNumberIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Bad Numbers
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>Bad Numbers</strong> is a compensation feature for players who end up with "unlucky" number combinations. In football squares, certain digits (like 2, 5, 8, and 9) rarely appear as the last digit of either team's score, making those squares less desirable. Bad Numbers payouts automatically compensate players whose squares match specific row-column combinations.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Right-Way Only:</strong> Bad Numbers are direction-specific. The combination "2-5" only pays when the row digit is 2 AND the column digit is 5 — it does NOT pay for the reverse (5-2).
        </Alert>

        <Paper elevation={0} sx={{ p: 3, bgcolor: 'warning.50', border: 1, borderColor: 'warning.main', mb: 3 }}>
          <Typography variant="h6" gutterBottom>How It Works</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText
                primary="Automatic payouts when board locks"
                secondary="Bad Numbers payouts are credited instantly when the board is locked, before the game even starts"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText
                primary="Fixed amount per bad square"
                secondary="Each bad square receives the same payout regardless of game outcome"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText
                primary="Right-way matches only"
                secondary="Combinations are direction-specific: 2-5 pays for row=2, col=5, but not for row=5, col=2"
              />
            </ListItem>
          </List>
        </Paper>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Football Bad Numbers (2, 5, 8, 9)</Typography>
        <Typography variant="body2" paragraph>
          The traditional "Football Bad" preset includes digits 2, 5, 8, and 9. These are historically the least likely to appear as a team's last digit in football scores. With 4 bad digits, there are <strong>16 bad squares</strong> (4 × 4 = 16) on the board.
        </Typography>

        {/* Bad Numbers Grid Visualization */}
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
            Bad Number Combinations (Football: 2, 5, 8, 9)
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Red cells show squares where BOTH the row AND column numbers are "bad"
          </Typography>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ width: 'auto', mx: 'auto', '& td, & th': { textAlign: 'center', p: 0.75, minWidth: 36 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.200', fontWeight: 'bold' }}></TableCell>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        bgcolor: [2, 5, 8, 9].includes(col) ? 'error.100' : 'grey.200',
                        fontWeight: 'bold',
                        color: [2, 5, 8, 9].includes(col) ? 'error.main' : 'text.primary',
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((row) => (
                  <TableRow key={row}>
                    <TableCell
                      sx={{
                        bgcolor: [2, 5, 8, 9].includes(row) ? 'error.100' : 'grey.200',
                        fontWeight: 'bold',
                        color: [2, 5, 8, 9].includes(row) ? 'error.main' : 'text.primary',
                      }}
                    >
                      {row}
                    </TableCell>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((col) => {
                      const isBadRow = [2, 5, 8, 9].includes(row);
                      const isBadCol = [2, 5, 8, 9].includes(col);
                      const isBadSquare = isBadRow && isBadCol;
                      return (
                        <TableCell
                          key={col}
                          sx={{
                            bgcolor: isBadSquare ? 'error.main' : isBadRow || isBadCol ? 'error.50' : 'background.paper',
                            color: isBadSquare ? 'white' : 'text.primary',
                            fontWeight: isBadSquare ? 'bold' : 'normal',
                          }}
                        >
                          {isBadSquare ? 'X' : ''}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Chip label="16 bad squares × $10 each = $160 total" color="error" />
          </Box>
        </Paper>

        {/* Bad Square Count Reference */}
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden', mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ p: 2, bgcolor: 'grey.100' }}>
            Bad Square Count Reference
          </Typography>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Bad Digits Count</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Bad Squares</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Example @ $10/square</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>2 digits (e.g., 2, 5)</TableCell>
                <TableCell align="center">4 squares</TableCell>
                <TableCell align="center">$40</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>3 digits (e.g., 2, 5, 9)</TableCell>
                <TableCell align="center">9 squares</TableCell>
                <TableCell align="center">$90</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'warning.50' }}>
                <TableCell><strong>4 digits (Football: 2, 5, 8, 9)</strong></TableCell>
                <TableCell align="center"><strong>16 squares</strong></TableCell>
                <TableCell align="center"><strong>$160</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>5 digits</TableCell>
                <TableCell align="center">25 squares</TableCell>
                <TableCell align="center">$250</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        <InfoCard title="Timing of Bad Numbers Payouts" variant="info">
          <Typography variant="body2">
            Bad Numbers payouts are processed <strong>automatically when the board is locked</strong>—before the game begins. This means players know their compensation immediately, and it shows up in their balance right away. If a board is unlocked, the Bad Numbers payouts are automatically reversed.
          </Typography>
        </InfoCard>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Note:</strong> Only claimed squares receive Bad Numbers payouts. Unclaimed bad squares do not generate payouts.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Putting It All Together */}
      <Box id="complete-example" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <CalcIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Complete Example
        </Typography>

        <Typography variant="body1" paragraph>
          Here's a complete payout configuration for a $1,000 pot with multiple features enabled:
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: 'primary.main' }}>
          <Typography variant="h6" gutterBottom>Example: Super Bowl Pool</Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Base Configuration</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Total Pot" secondary="$1,000 (100 × $10)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Organizer Profit" secondary="10% = $100" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Payout Pool" secondary="$900" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Quarter Payouts</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Q1" secondary="$90 (10%)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Q2/Halftime" secondary="$180 (20%)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Q3" secondary="$90 (10%)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Q4/Final" secondary="$540 (60%)" />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Wrong Way Payouts</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Q1-Q3 Wrong Way" secondary="$10 each" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Q4 Wrong Way" secondary="$50" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>+5 Rule (Right Way)</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Q1-Q3 Right Way +5" secondary="$5 each" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Q4 Right Way +5" secondary="$25" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This configuration allocates $900 total: the Right Way amounts shown are the maximum if no Wrong Way or +5 winners exist. When Wrong Way and +5 payouts occur, they come from the same pool. For example, if Q1 has all three winners: Right Way gets $75, Wrong Way gets $10, and +5 gets $5 = <strong>$90 total for Q1</strong>.
        </Typography>
      </Box>
    </SupportLayout>
  );
};

export default PayoutsPage;
